import React, { useState, useRef, useEffect } from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { MediaStage } from "@/core/types/mediaTypes";
import { MEDIA_STAGE_CONFIG, MEDIA_STAGE_ORDER } from "@/core/MediaItem/mediaStageConfig";
import { ChevronDownIcon } from "@/components/icons";

interface StageDropdownProps {
  mediaItem: MediaItem;
  onStageChange: (newStage: string, gridPosition?: number) => Promise<void>;
  isChanging: boolean;
  disabled?: boolean;
}

const STAGES = MEDIA_STAGE_ORDER.map((stage) => ({
  value: stage,
  label: MEDIA_STAGE_CONFIG[stage].label,
  color: MEDIA_STAGE_CONFIG[stage].color,
}));

export const StageDropdown: React.FC<StageDropdownProps> = ({
  mediaItem,
  onStageChange,
  isChanging,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<"below" | "above">(
    "below",
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentStage = STAGES.find((stage) => stage.value === mediaItem.stage);

  useEffect(() => {
    if (isOpen && buttonRef.current && dropdownRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.offsetHeight || 200; // Estimate if not rendered
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      // If there's not enough space below but there is above, position above
      if (
        spaceBelow < dropdownHeight + 10 &&
        spaceAbove > dropdownHeight + 10
      ) {
        setDropdownPosition("above");
      } else {
        setDropdownPosition("below");
      }
    }
  }, [isOpen]);

  const handleStageSelect = async (newStage: string) => {
    setIsOpen(false);
    await onStageChange(newStage);
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleDropdownClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  if (disabled || isChanging) {
    return (
      <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-sm text-xs">
        <div
          className={`w-2 h-2 rounded-full ${currentStage?.color || "bg-gray-400"}`}
        />
        <span className="text-gray-600">
          {isChanging ? "Spostamento..." : currentStage?.label || mediaItem.stage}
        </span>
        {isChanging && (
          <div className="w-3 h-3 border border-gray-400 border-t-gray-600 rounded-full animate-spin ml-1" />
        )}
      </div>
    );
  }

  return (
    <div className="relative" onClick={handleDropdownClick}>
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        className="flex items-center gap-1 px-2 py-1 bg-white/90 hover:bg-white rounded-sm text-xs border border-gray-200 shadow-xs transition-colors"
      >
        <div
          className={`w-2 h-2 rounded-full ${currentStage?.color || "bg-gray-400"}`}
        />
        <span className="font-medium">
          {currentStage?.label || mediaItem.stage}
        </span>
        <ChevronDownIcon className={`w-3 h-3 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          />

          <div
            ref={dropdownRef}
            className={`absolute left-0 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-50 ${
              dropdownPosition === "above"
                ? "bottom-full mb-1"
                : "top-full mt-1"
            }`}
            onClick={handleDropdownClick}
          >
            <div className="py-1">
              {STAGES.map((stage, index) => (
                <button
                  key={stage.value}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleStageSelect(stage.value);
                  }}
                  disabled={stage.value === mediaItem.stage}
                  className={`
                    w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-gray-50 transition-colors ${
                      stage.value === mediaItem.stage
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "text-gray-700"
                    } ${index === 0 ? "rounded-t" : ""} ${
                      index === STAGES.length - 1 ? "rounded-b" : ""
                    }`}
                  role="option"
                  aria-selected={stage.value === mediaItem.stage}
                >
                  <div
                    className={`w-2.5 h-2.5 rounded-full ${stage.color} transition-all duration-200 ${
                      stage.value === mediaItem.stage
                        ? "ring-2 ring-blue-300 ring-offset-1"
                        : "group-hover:scale-110"
                    }`}
                  />
                  <div className="flex-1">
                    <div className="font-medium">{stage.label}</div>
                  </div>
                  {stage.value === mediaItem.stage && (
                    <span className="ml-auto text-gray-400">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
